import { inject, Injectable, signal } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { ReportCriteria, ReportField, ReportType } from '../Domain/reports.model';
import { GET_REPORTS } from '@modules/reports/Services/config/reports.config';
import { map } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CollectorModel } from '@admin/modules/collectors/model/collectors.model';
import { Portfolio } from '@modules/operations/Domain/operation.model';
import { TimezoneService } from '@shared/services/timezone.service';

@Injectable({ providedIn: 'root' })
export class ReportService {
  private timezoneService = inject(TimezoneService);
  private _data = signal<any>(null);
  data = this._data;

  constructor(
    private apollo: Apollo,
    private fb: FormBuilder,
  ) {}

  fetchReport(type: ReportType, criteria: ReportCriteria) {
    const fullCriteria = {
      types: [type],
      expiresAt: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString(),
      ...criteria,
    };

    return this.apollo
      .query({
        query: GET_REPORTS,
        variables: { criteria: fullCriteria },
        fetchPolicy: 'network-only',
      })
      .pipe(map((res) => res.data));
  }

  buildForm(fields: ReportField[], initialCriteria: Record<string, any> = {}): FormGroup {
    const controls: Record<string, any> = {};

    fields.forEach((field) => {
      let defaultValue = initialCriteria[field.name] ?? null;
      let validators = field.required ? [Validators.required] : [];

      if (field.type === 'dateRange') {
        const start = defaultValue?.start || new Date();
        const end = defaultValue?.end || new Date();
        controls[field.name] = [[start, end], validators];
      } else if (field.type === 'date') {
        if (field.dateView === 'month' && !defaultValue) {
          const now = new Date();
          defaultValue = new Date(now.getFullYear(), now.getMonth(), 1);
        }
        controls[field.name] = [defaultValue, validators];
      } else if (field.type === 'dropdown') {
        if (field.required && !defaultValue) {
          const options = this.getDropdownOptions([field])[field.name] || [];
          defaultValue = options.length ? options[0].value : null;
        }
        controls[field.name] = [defaultValue, validators];
      } else {
        controls[field.name] = [defaultValue, validators];
      }
    });

    return this.fb.group(controls);
  }

  mapFormValuesToCriteria(
    formValues: Record<string, any>,
    fields: ReportField[],
  ): Record<string, any> {
    const criteria: Record<string, any> = { ...formValues };

    const formatDate = (date: any, endOfDay = false): string | null => {
      if (!(date instanceof Date)) date = new Date(date);
      if (isNaN(date.getTime())) return null;
      if (endOfDay) date.setHours(23, 59, 59, 0);
      else date.setHours(0, 0, 0, 0);
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    for (const field of fields) {
      const value = formValues[field.name];
      if (!value) continue;

      if (field.isMonthRange && value instanceof Date) {
        const year = value.getFullYear();
        const month = value.getMonth();
        criteria['startDate'] = formatDate(new Date(year, month, 1, 0, 0, 0));
        criteria['endDate'] = formatDate(new Date(year, month + 1, 0, 23, 59, 59), true);
        delete criteria[field.name];
      } else if (field.type === 'date' && value instanceof Date && !field.isMonthRange) {
        criteria[field.name] = formatDate(value);
      } else if (field.type === 'dateRange' && Array.isArray(value) && value.length === 2) {
        const [start, end] = value;
        criteria['startDate'] = this.formatDateWithTimezone(start);
        criteria['endDate'] = this.formatDateWithTimezone(end, true);
        delete criteria[field.name];
      } else if (field.name === 'campaignId' && Array.isArray(value)) {
        criteria[field.name] = value[0] ?? null;
      }
    }

    if (criteria['expiresAt']) {
      criteria['expiresAt'] = formatDate(criteria['expiresAt'], true);
    }

    return criteria;
  }

  getDropdownOptions(
    fields: ReportField[],
    collectors: CollectorModel[] = [],
    portfolios: Portfolio[] = [],
    operationStatus: any[] = [],
  ): Record<string, any[]> {
    const options: Record<string, any[]> = {};

    for (const field of fields) {
      if (field.type !== 'dropdown') continue;

      switch (field.name) {
        case 'collectorId':
          options[field.name] = collectors.map((c) => ({ label: c.name, value: c.id }));
          break;

        case 'portfolioId':
          options[field.name] = portfolios.map((p) => ({ label: p.name, value: p.id }));
          break;

        case 'ignoreOperationStatus':
          options[field.name] = operationStatus.map((s) => ({ label: s.name, value: s.id }));
          break;

        case 'months':
          options[field.name] = Array.from({ length: 20 }, (_, i) => ({
            label: (i + 1).toString(),
            value: i + 1,
          }));
          break;

        default:
          options[field.name] = field.options || [];
      }
    }

    return options;
  }

  //TODO: extract to utils
  private formatDateWithTimezone(date: Date, isEnd: boolean = false): string | null {
    if (!date) return null;

    return isEnd
      ? this.timezoneService.convertToEndOfDay(date).toISOString()
      : this.timezoneService.convertToStartOfDay(date).toISOString();
  }
}
